import { Button, colors, LoadingSpinner, Modal, TextInput } from 'shared-library';
import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { fetchPiiResource } from 'packages/apis';
import { PortalRoot } from 'packages/portal-root';

import { Column, Row } from '../Layout';
import { AmountToPay } from '../AmountToPay';

import { v4 as uuidv4 } from 'uuid';

const InvoiceRow = styled(Row)`
  display: grid;
  grid-template-columns: repeat(3, 33.3%);
`;

const InvoiceDetailsFromSalesforce = ({
  checkSalesforceAccount,
  closeInvoiceDetailsModal,
  invoicePayer,
  updateTransactionWithInvoicePayer,
}) => {
  const requiredFields = [
    'name',
    'address',
    'postalCode',
    'state',
    'country',
    'city',
    'phone',
    'email',
  ];

  const hasAllInvoicePayerDetails = () => {
    const verdict = ['isValid', ...requiredFields].every((field) => {
      // TODO: Check if other variants of United Kingom may come from SF?
      // UK does not have states
      if (field === 'state' && invoicePayer.country === 'United Kingdom') {
        return true;
      }
      return invoicePayer[field];
    });
    return invoicePayer && verdict;
  };
  const disabledConfirmButton = !hasAllInvoicePayerDetails();

  return (
    <>
      <PortalRoot>
        <Modal onClose={closeInvoiceDetailsModal} title="Invoice Payer Details">
          {!invoicePayer?.isValid && <p>This Salesforce Account is not a Provider or Enterprise</p>}

          <>
            {requiredFields.map((field) => (
              <Row>
                <Column>
                  {field.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase())}
                </Column>
                <Column>{invoicePayer[field]}</Column>
              </Row>
            ))}
          </>
          <Row>
            <Button
              onClick={updateTransactionWithInvoicePayer}
              size="medium"
              disabled={disabledConfirmButton}
              variant="primary"
            >
              Confirm Invoice Payer Details
            </Button>
            <Button onClick={checkSalesforceAccount} size="medium" variant="secondary">
              Refresh
            </Button>
          </Row>
        </Modal>
      </PortalRoot>
    </>
  );
};

export const InvoicePayment = ({ transaction, setTransaction }) => {
  const [invoicePayerSalesforceAccountId, setInvoicePayerSalesforceAccountId] = useState('');
  const [showInvoiceDetailsModal, setShowInvoiceDetailsModal] = useState(false);
  const [invoicePayer, setInvoicePayer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sfAccountErrorMsg, setSfAccountErrorMsg] = useState('');

  const updateTransactionWithInvoicePayer = useMemo(() => {
    return () => {
      setTransaction({
        ...transaction,
        status: 'pending',
        paymentType: 'stripe invoice',
        stripeInvoiceInput: {
          salesforceAccountId: invoicePayerSalesforceAccountId,
          ...invoicePayer,
        },
      });
      setShowInvoiceDetailsModal(false);
    };
  });

  const checkSalesforceAccount = async () => {
    setSfAccountErrorMsg('');
    setLoading(true);
    try {
      const { parsedBody } = await fetchPiiResource(
        `/api/v1/salesforce/person/${invoicePayerSalesforceAccountId}?format_for_invoicing=true`,
      );
      if (parsedBody) {
        setInvoicePayer(parsedBody);
        setShowInvoiceDetailsModal(true);
      }
    } catch (error) {
      if (error.message === 'NOT FOUND') {
        setSfAccountErrorMsg('Salesforce Account not found');
      }
      else {
        const uuid = uuidv4();
        const errorMsg = 'Error fetching Salesforce account (Error ID: ' + uuid + ')';
        setSfAccountErrorMsg(errorMsg);
        console.error(errorMsg, error);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {showInvoiceDetailsModal && (
        <InvoiceDetailsFromSalesforce
          closeInvoiceDetailsModal={() => setShowInvoiceDetailsModal(false)}
          invoicePayer={invoicePayer}
          updateTransactionWithInvoicePayer={updateTransactionWithInvoicePayer}
          checkSalesforceAccount={checkSalesforceAccount}
        />
      )}
      <InvoiceRow>
        <AmountToPay transaction={transaction} setTransaction={setTransaction} />
        <Column />
        <Column>
          <TextInput
            required
            label="Invoice Payer Salesforce Account ID"
            placeholder="Enter Salesforce Account ID"
            value={invoicePayerSalesforceAccountId}
            type="text"
            id="invoicePayerSalesforceAccountId"
            onChange={(str) => {
              setInvoicePayerSalesforceAccountId(str);
              setSfAccountErrorMsg('');
            }}
          />
          <p style={{ color: 'red' }}>{sfAccountErrorMsg}</p>
        </Column>
      </InvoiceRow>
      <InvoiceRow>
        <Column />
        <Column />
        <Column>
          <Button
            disabled={loading || !invoicePayerSalesforceAccountId}
            size="medium"
            onClick={checkSalesforceAccount}
            variant="primary"
          >
            {loading ? (
              <LoadingSpinner color={colors.white} size="small" />
            ) : (
              'Check Salesforce Account for Invoicing Details'
            )}
          </Button>
        </Column>
      </InvoiceRow>
    </>
  );
};

export default InvoicePayment;
